<template>
    <q-btn
        outline
        color="white"
        text-color="secondary"
        @click="handleMediaInput"
        size="1.3rem"
        :class="{
            'full-width q-mt-sm': $q.screen.xs
        }"
        :disabled="hasReachedMaxMedia"
        :loading="isLoading"
        icon="fa-light fa-camera"
    >
        <q-tooltip v-if="!hasReachedMaxMedia">
            {{ $t('Add a photo to this event.') }}
        </q-tooltip>
        <q-tooltip v-else>
            {{
                $t('The maximum number of media ({max}) has been reached.', {
                    max: MAX_MEDIA_COUNT
                })
            }}
        </q-tooltip>
    </q-btn>
    <!-- Hidden file input for image upload on desktop -->
    <q-file
        ref="fileInput"
        :model-value="internalMedias"
        style="display: none"
        multiple
        @update:model-value="onFileChange($event)"
        accept="image/jpg, image/jpeg, image/png"
        :max-files="MAX_MEDIA_COUNT - mediaCount"
    />
</template>

<script>
import { MAX_MEDIA_COUNT } from '@utils/mediaManager.js';
import { compressImage, validateImage } from '@utils/mediaManager.js';

export default {
    name: 'MediaInputManager',
    emits: ['update:add'],
    props: {
        mediaCount: {
            type: Number,
            default: 0
        }
    },
    unmounted() {
        // We need to revoke the object URL to free up memory, otherwise the browser
        // will keep them in memory until the user closes the tab.
        // See https://developer.mozilla.org/en-US/docs/Web/API/URL/revokeObjectURL_static
        this.internalMedias.forEach(media => URL.revokeObjectURL(media.url));
    },
    data() {
        return {
            internalMedias: [],
            isLoading: false,
            MAX_MEDIA_COUNT,
            MAX_FILE_SIZE: 1024 * 1024 * 5 // 5MB
        };
    },
    computed: {
        hasReachedMaxMedia() {
            return this.mediaCount >= this.MAX_MEDIA_COUNT;
        }
    },
    methods: {
        compressImage,
        validateImage,
        /**
         * Converts a webPath (URL) to a File object.
         * @param {string} webPath - The URL of the image to fetch.
         * @param {string} fileName - The desired filename for the File object.
         * @returns {Promise<File>} Promise resolving to a File object representing the image.
         * @example
         * const file = await this.webPathToFile(image.webPath, 'photo.jpeg');
         */
        async webPathToFile(webPath, fileName) {
            const response = await fetch(webPath);
            const blob = await response.blob();
            // Infer mime type from blob or fallback to jpeg
            const mimeType = blob.type || 'image/jpeg';
            return new File([blob], fileName, { type: mimeType });
        },
        /**
         * Handles media input (images) selection.
         */
        async handleMediaInput() {
            if (this.$q.platform.is.capacitor) {
                const image = await this.$capturePhoto();
                console.log('Image:', JSON.stringify(image));
                // Convert the Camera image (webPath) to File before processing
                if (image && image.webPath) {
                    try {
                        const file = await this.webPathToFile(
                            image.webPath,
                            `photo_${Date.now()}.${image.format || 'jpeg'}`
                        );
                        this.processImageFiles([file]);
                    } catch (err) {
                        this.$notifyError(
                            this.$t('Failed to process captured image.')
                        );
                        console.error('webPathToFile error:', err);
                    }
                } else {
                    this.$notifyError(this.$t('No image captured.'));
                }
            }

            if (this.$q.platform.is.desktop) {
                // Open the native file explorer
                this.$refs.fileInput.pickFiles();
            }
        },
        /**
         * Handles the file selection event from the hidden file q-file
         * for desktop
         * @param {Event} event - The change event from the file input.
         */
        onFileChange(event) {
            if (!event.length) return;

            this.processImageFiles(event);
        },
        /**
         * Processes an array of image files: compresses, validates, and adds them to internalMedias.
         * Notifies error if a non-image file is found. Emits the updated media list after processing.
         * @param {FileList|Array<File>} files - Array or FileList of selected files to process.
         */
        async processImageFiles(files) {
            const promises = [];
            this.isLoading = true;
            const { isValid, errors } = this.validateImage(files);
            if (!isValid) {
                for (const error of errors) {
                    this.$notifyError(error);
                }
                this.isLoading = false;
                return;
            }
            // For each file, compress it and add it to the internalMedias array
            for (const file of files) {
                promises.push(
                    this.compressImage(file).then(compressedImage => {
                        this.internalMedias.push({
                            file: compressedImage,
                            url: URL.createObjectURL(compressedImage)
                        });
                    })
                );
            }
            // Wait all promises to resolve and emit the updated medias only once
            try {
                await Promise.all(promises);
                this.$emit('update:add', this.internalMedias);
                this.internalMedias = [];
            } catch (error) {
                console.error('Error compressing image: ', error);
                this.$notifyError(
                    this.$t(
                        'An error occurred while processing the image. Please ensure the file is a valid image format and try again.'
                    )
                );
            } finally {
                this.isLoading = false;
            }
        }
    }
};
</script>
